import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { useQuery } from "@tanstack/react-query";
import { DataType } from "@/types/enums/DataType";
import type { Field } from "@/types/Field";
import { getFields } from "@/lib/api/handlers/ViewFilterHandling";
import { filterConditionFields } from "@/components/ConditionBuilder/condition-utils";

/**
 * Built-in guest facets a cohort can be cut by. Mirror of main-backend
 * `CohortDimensions.BUILT_IN` — the backend counts a cohort-share KPI and the
 * analysis' exact target-vs-actual over exactly these (plus
 * `additionalFields.<name>`). Contact PII is deliberately absent.
 */
export const COHORT_DIMENSIONS = [
  "guestType",
  "gender",
  "title",
  "jobTitle",
  "company",
  "city",
  "zipCode",
  "country",
  "language",
  "origin",
  // The guest's own answer, the organiser's admission and the scan — countable
  // like any field (goals "abstract" wave 2026-10-05). Named by the goals
  // vocabulary, not the guest-list column ("Teilnahme" read as the answer).
  "state",
  "acceptanceState",
  "checkedIn",
] as const;

/** Dimensions whose NAME comes from goals.cohortDimensions, not the guest-list column. */
export const GOALS_NAMED_DIMENSIONS: ReadonlySet<string> = new Set(["state", "acceptanceState", "checkedIn"]);

export const COHORT_AF_PREFIX = "additionalFields.";

/**
 * A protection level that keeps a field's values away from the AI and out of
 * every cohort: anything but NORMAL (absent = NORMAL). Never one constant —
 * SENSITIVE and ART9 gate identically (`Field.sensitivity`), and a level this
 * bundle does not know yet must not read as unprotected.
 */
export function isRestrictedSensitivity(level: string | null | undefined): boolean {
  return !!level && level !== "NORMAL";
}

/**
 * Whether a custom guest field may be a cohort facet — mirror of main-backend
 * `EventGoalResource.cohortFieldDefinitions` → `FacetPlan.isExcluded`: no
 * IMAGE (binary objects), no EMAIL (contact PII the analysis leaves out), no
 * restricted sensitivity. The backend answers a KPI, a group or a NEW target
 * on any of them with 400 `kpiDimensionInvalid` / `kpiBaseInvalid`, so a
 * picker must not offer one (ship 2026-10-06, audit r1 LOW-1). GUEST fields
 * only (absent = GUEST): a cohort counts guests, and the template host reads
 * every target's fields — a speaker or sponsor field would count 0 % forever
 * (audit r2 LOW-5; the backend's `cohortFieldDefinitions` reads GUEST only).
 */
export function isCohortPickableField(field: Field): boolean {
  return (
    !!field.additionalField &&
    (field.appliesTo ?? "GUEST") === "GUEST" &&
    field.dataType !== DataType.IMAGE &&
    field.dataType !== DataType.EMAIL &&
    !isRestrictedSensitivity(field.sensitivity)
  );
}

/** The custom-field facet options (key/label, sorted) of a definition list — ONE rule for every host. */
export function cohortFieldOptionsOf(fields: Field[]): { key: string; label: string }[] {
  return filterConditionFields(fields)
    .filter(isCohortPickableField)
    .map((f) => ({ key: `${COHORT_AF_PREFIX}${f.name}`, label: f.translation || f.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** The event's guest field definitions — the builders' query key, so a visited builder page dedupes the fetch. */
function guestFieldsQuery(eventId: string | undefined, enabled: boolean) {
  return {
    queryKey: ["additionalFields", eventId, "guest"],
    queryFn: () => getFields("guest", eventId),
    enabled: !!eventId && enabled,
    staleTime: 60_000,
  };
}

type Options = {
  /** Caller-supplied additional-field options (key/label) — replaces the event
   *  field query for hosts without an event (event templates). */
  fieldOptionsOverride?: { key: string; label: string }[];
  /** Persisted dimensions to keep selectable even when no longer offered
   *  (deleted additional field, legacy free text) — opening an editor must
   *  never silently rewrite a row. */
  extraValues?: Array<string | undefined | null>;
  /** Offer the "" (automatic) option — cohort TARGETS may leave the facet to
   *  the analysis; a cohort KPI may not. */
  includeAuto?: boolean;
};

/**
 * The facet options + labels shared by the cohort-target rows and the
 * cohort-share KPI editor. Shares the builders' `["additionalFields", eventId,
 * "guest"]` query key, so an already-visited builder page dedupes the fetch.
 */
export function useCohortDimensionOptions(eventId: string | undefined, options: Options = {}) {
  const { fieldOptionsOverride, extraValues, includeAuto = true } = options;
  const t = useTranslations("goals");
  const tFields = useTranslations("guest-fields");

  const fieldsQuery = useQuery(guestFieldsQuery(eventId, !fieldOptionsOverride));

  const additionalFieldOptions = useMemo(() => {
    if (fieldOptionsOverride) return fieldOptionsOverride;
    return cohortFieldOptionsOf(Array.isArray(fieldsQuery.data) ? fieldsQuery.data : []);
  }, [fieldsQuery.data, fieldOptionsOverride]);

  // What the backend would accept for a NEW row (built-ins + the pickable
  // custom fields) — without the persisted extras, which stay selectable only
  // so an editor never rewrites a stored row. The "within group" picker
  // offers the goal's audience rows only when their facet is in here.
  const offeredDimensions = useMemo(
    () => new Set<string>([...COHORT_DIMENSIONS, ...additionalFieldOptions.map((o) => o.key)]),
    [additionalFieldOptions],
  );

  // Joined to a stable string: callers pass a fresh array every render.
  const extrasKey = (extraValues ?? []).filter(Boolean).join("\n");

  const dimensionOptions = useMemo(() => {
    const base = [
      ...(includeAuto ? [""] : []),
      ...COHORT_DIMENSIONS,
      ...additionalFieldOptions.map((o) => o.key),
    ];
    const known = new Set<string>(base);
    const extras = extrasKey ? extrasKey.split("\n").filter((d) => !known.has(d)) : [];
    return [...base, ...Array.from(new Set(extras))];
  }, [additionalFieldOptions, extrasKey, includeAuto]);

  const dimensionLabels = useMemo(
    () =>
      Object.fromEntries([
        ["", t("targets.dimensionAuto")],
        ...COHORT_DIMENSIONS.map((d) => [
          d,
          GOALS_NAMED_DIMENSIONS.has(d) ? t(`cohortDimensions.${d}`) : tFields(d),
        ]),
        ...additionalFieldOptions.map((o) => [o.key, o.label]),
        ...dimensionOptions
          .filter(
            (d) =>
              d !== "" &&
              !(COHORT_DIMENSIONS as readonly string[]).includes(d) &&
              !additionalFieldOptions.some((o) => o.key === d),
          )
          .map((d) => [d, d.startsWith(COHORT_AF_PREFIX) ? d.slice(COHORT_AF_PREFIX.length) : d]),
      ]) as Record<string, string>,
    [t, tFields, additionalFieldOptions, dimensionOptions],
  );

  return { dimensionOptions, dimensionLabels, offeredDimensions, loading: fieldsQuery.isLoading };
}
